/*
 * ONE platform-identity → hero-image mapping, mirrored byte-for-byte across
 * Cortex and Quantum.
 *
 * Optimus already carried a PLATFORM_ICON_MAP inside each portal's Optimus.js,
 * keyed by a bare `toLowerCase()`. That is enough for the tab strip, whose ids
 * the page produced itself, but not for plan-vs-performance rows, whose
 * platform labels arrive from the approved plan and the grading contract in
 * whatever form those declared them: "Connected TV", "Programmatic",
 * "Google Search", "Truth Social".
 *
 * Two concrete misses in the legacy map, both silent:
 *
 *   normalize("google_search")  -> "google_search"  ; map key is "googlesearch"
 *   normalize("truth_social")   -> "truth_social"   ; map key is "truthsocial"
 *
 * so a row for either rendered no image at all. This module keys on
 * `normalizeOptimusPlatformKey` — the canonical platform identity mirrored from
 * the backend grading contract, and the same normalizer the rows themselves are
 * scoped by — so an image can never disagree with the row it sits beside.
 *
 * Aliases are therefore NOT re-declared here. "Connected TV" resolves to `ctv`
 * because the canonical normalizer says so, not because this file repeats the
 * fact. Adding a synonym belongs in the contract, once, for every consumer.
 */

import { normalizeOptimusPlatformKey } from "./optimusGradingContract";

import facebookIcon from "../assets/facebook.jpg";
import instagramIcon from "../assets/instagram.jpg";
import metaIcon from "../assets/meta.jpg";
import tiktokIcon from "../assets/tiktok.jpg";
import youtubeIcon from "../assets/youtube.jpg";
import googleIcon from "../assets/google.jpg";
import openaiIcon from "../assets/openAI.png";
import redditIcon from "../assets/reddit.jpg";
import amazonIcon from "../assets/amazon.jpg";
import yahooIcon from "../assets/yahoo.png";
import viantIcon from "../assets/viant.png";
import { normalizeProviderSurfaceKey } from "./providerSurfaceRegistry";
import audioIcon from "../assets/audio.jpg";
import ctvIcon from "../assets/ctv.jpg";
import videoIcon from "../assets/video.jpg";
import mobileIcon from "../assets/mobile.jpg";
import displayIcon from "../assets/display.jpg";
import doohIcon from "../assets/dooh.gif";
import emailIcon from "../assets/email.jpg";
import ingameIcon from "../assets/ingame.jpg";
import rumbleIcon from "../assets/rumble-logo.jpg";
import truthSocialIcon from "../assets/truth-social-logo.jpg";
import xIcon from "../assets/x.jpg";

/*
 * Keys are CANONICAL platform identities (normalizeOptimusPlatformKey output),
 * which is why they carry underscores: `google_search`, not `googlesearch`.
 */
export const OPTIMUS_PLATFORM_HERO_IMAGES = Object.freeze({
  facebook: facebookIcon,
  instagram: instagramIcon,
  /* Meta's context surfaces share the parent mark; they have none of their own. */
  meta: metaIcon,
  audience_network: metaIcon,
  messenger: metaIcon,
  tiktok: tiktokIcon,
  youtube: youtubeIcon,
  google_search: googleIcon,
  chatgpt_search: openaiIcon,
  reddit: redditIcon,
  amazon: amazonIcon,
  yahoo: yahooIcon,
  yahoo_dsp: yahooIcon,
  yahoodsp: yahooIcon,
  viant: viantIcon,
  audio: audioIcon,
  ctv: ctvIcon,
  video: videoIcon,
  mobile: mobileIcon,
  display: displayIcon,
  dooh: doohIcon,
  email: emailIcon,
  ingame: ingameIcon,
  rumble: rumbleIcon,
  truth_social: truthSocialIcon,
  x: xIcon,
});

/**
 * The hero image for one platform, resolved through the canonical identity.
 *
 * Returns `{ key, src, hasImage }`. `src` is null for a platform Optimus can
 * legitimately render but ships no artwork for — the caller draws a branded
 * monogram rather than a broken image or a collapsed gap, so the row keeps its
 * height either way.
 */
export function resolveOptimusPlatformHeroImage(platform) {
  const key = normalizeProviderSurfaceKey(platform) || normalizeOptimusPlatformKey(platform);
  const src = key ? OPTIMUS_PLATFORM_HERO_IMAGES[key] || null : null;
  return { key: key || null, src, hasImage: Boolean(src) };
}

/**
 * The one or two letters a fallback circle shows. Derived from the LABEL the
 * row already displays, so the fallback still reads as that platform rather
 * than as a generic placeholder.
 */
export function optimusPlatformHeroInitials(label, platform) {
  const source = String(label || platform || "").trim();
  if (!source) return "?";
  const words = source.split(/[\s_\-/]+/).filter(Boolean);
  if (!words.length) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}
